import { useEffect } from "react";

const DEFAULT_ALTERNATE_LINKS = [];

const ensureMeta = (selector, createAttributes) => {
  if (typeof document === "undefined") return null;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(createAttributes || {}).forEach(([name, value]) => {
      element.setAttribute(name, value);
    });
    document.head.appendChild(element);
  }
  return element;
};

const ensureLink = (rel) => {
  if (typeof document === "undefined") return null;
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  return element;
};

const removeManagedAlternateLinks = () => {
  if (typeof document === "undefined") return;
  document.head
    .querySelectorAll('link[data-xima-seo="hreflang"]')
    .forEach((element) => element.remove());
};

const appendAlternateLinks = (alternateLinks) => {
  if (typeof document === "undefined" || !Array.isArray(alternateLinks)) return;
  removeManagedAlternateLinks();
  alternateLinks
    .filter((item) => item?.href && item?.hrefLang)
    .forEach((item) => {
      const element = document.createElement("link");
      element.setAttribute("rel", "alternate");
      element.setAttribute("hreflang", item.hrefLang);
      element.setAttribute("href", item.href);
      element.setAttribute("data-xima-seo", "hreflang");
      document.head.appendChild(element);
    });
};

const removeSeoScript = () => {
  if (typeof document === "undefined") return;
  const current = document.getElementById("xima-learn-json-ld");
  if (current) current.remove();
};

const appendSeoScript = (structuredData) => {
  if (typeof document === "undefined" || !structuredData) return;
  removeSeoScript();
  const script = document.createElement("script");
  script.id = "xima-learn-json-ld";
  script.type = "application/ld+json";
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
};

const setMetaContent = (element, value) => {
  if (!element || value == null) return;
  element.setAttribute("content", value);
};

const SeoMeta = ({
  title,
  description,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = "article",
  ogImage,
  robots,
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  alternateLinks = DEFAULT_ALTERNATE_LINKS,
  datePublished,
  dateModified,
  structuredData,
}) => {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousTitle = document.title;
    const canonicalElement = ensureLink("canonical");
    const previousCanonical = canonicalElement?.getAttribute("href") || "";

    const descriptionElement = ensureMeta('meta[name="description"]', {
      name: "description",
    });
    const previousDescription = descriptionElement?.getAttribute("content") || "";

    const ogTitleElement = ensureMeta('meta[property="og:title"]', {
      property: "og:title",
    });
    const previousOgTitle = ogTitleElement?.getAttribute("content") || "";

    const ogDescriptionElement = ensureMeta('meta[property="og:description"]', {
      property: "og:description",
    });
    const previousOgDescription = ogDescriptionElement?.getAttribute("content") || "";

    const ogTypeElement = ensureMeta('meta[property="og:type"]', {
      property: "og:type",
    });
    const previousOgType = ogTypeElement?.getAttribute("content") || "";

    const ogUrlElement = ensureMeta('meta[property="og:url"]', {
      property: "og:url",
    });
    const previousOgUrl = ogUrlElement?.getAttribute("content") || "";

    const ogImageElement = ensureMeta('meta[property="og:image"]', {
      property: "og:image",
    });
    const previousOgImage = ogImageElement?.getAttribute("content") || "";

    const robotsElement = ensureMeta('meta[name="robots"]', {
      name: "robots",
    });
    const previousRobots = robotsElement?.getAttribute("content") || "";

    const twitterCardElement = ensureMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
    });
    const previousTwitterCard = twitterCardElement?.getAttribute("content") || "";

    const twitterTitleElement = ensureMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
    });
    const previousTwitterTitle = twitterTitleElement?.getAttribute("content") || "";

    const twitterDescriptionElement = ensureMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
    });
    const previousTwitterDescription = twitterDescriptionElement?.getAttribute("content") || "";

    const twitterImageElement = ensureMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
    });
    const previousTwitterImage = twitterImageElement?.getAttribute("content") || "";

    const articlePublishedElement = ensureMeta('meta[property="article:published_time"]', {
      property: "article:published_time",
    });
    const previousArticlePublished = articlePublishedElement?.getAttribute("content") || "";

    const articleModifiedElement = ensureMeta('meta[property="article:modified_time"]', {
      property: "article:modified_time",
    });
    const previousArticleModified = articleModifiedElement?.getAttribute("content") || "";

    if (title) document.title = title;
    if (canonicalUrl && canonicalElement) canonicalElement.setAttribute("href", canonicalUrl);
    setMetaContent(descriptionElement, description);
    setMetaContent(ogTitleElement, ogTitle || title);
    setMetaContent(ogDescriptionElement, ogDescription || description);
    setMetaContent(ogTypeElement, ogType);
    setMetaContent(ogUrlElement, canonicalUrl);
    setMetaContent(ogImageElement, ogImage);
    setMetaContent(robotsElement, robots);
    setMetaContent(twitterCardElement, twitterCard);
    setMetaContent(twitterTitleElement, twitterTitle || ogTitle || title);
    setMetaContent(twitterDescriptionElement, twitterDescription || ogDescription || description);
    setMetaContent(twitterImageElement, twitterImage || ogImage);
    setMetaContent(articlePublishedElement, datePublished);
    setMetaContent(articleModifiedElement, dateModified);
    appendAlternateLinks(alternateLinks);
    appendSeoScript(structuredData);

    return () => {
      document.title = previousTitle;
      if (canonicalElement) {
        if (previousCanonical) canonicalElement.setAttribute("href", previousCanonical);
        else canonicalElement.removeAttribute("href");
      }
      setMetaContent(descriptionElement, previousDescription);
      setMetaContent(ogTitleElement, previousOgTitle);
      setMetaContent(ogDescriptionElement, previousOgDescription);
      setMetaContent(ogTypeElement, previousOgType);
      setMetaContent(ogUrlElement, previousOgUrl);
      setMetaContent(ogImageElement, previousOgImage);
      setMetaContent(robotsElement, previousRobots);
      setMetaContent(twitterCardElement, previousTwitterCard);
      setMetaContent(twitterTitleElement, previousTwitterTitle);
      setMetaContent(twitterDescriptionElement, previousTwitterDescription);
      setMetaContent(twitterImageElement, previousTwitterImage);
      setMetaContent(articlePublishedElement, previousArticlePublished);
      setMetaContent(articleModifiedElement, previousArticleModified);
      removeManagedAlternateLinks();
      removeSeoScript();
    };
  }, [
    alternateLinks,
    canonicalUrl,
    dateModified,
    datePublished,
    description,
    ogDescription,
    ogImage,
    ogTitle,
    ogType,
    robots,
    structuredData,
    title,
    twitterCard,
    twitterDescription,
    twitterImage,
    twitterTitle,
  ]);

  return null;
};

export default SeoMeta;
